// src/PromptInput.js - Updated with beautiful upgrade messages
import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelloSentences from './HelloSentences';
import './PromptInput.css';

function PromptInput({
  user,
  usageStats,
  planTracker,
  promptText,
  setPromptText,
  selectedFile,
  setSelectedFile,
  isDynamicMode,
  setIsDynamicMode,
  isAnyProcessing,
  isBulkProcessing,
  bulkProgress,
  processingError,
  pdfError,
  bulkError,
  canUseDynamic,
  hasDynamicQRLeft,
  dynamicQRLimit,
  dynamicQRRemaining,
  dynamicQRUsed,
  onPromptSubmit,
  onPDFUploadClick,
  onCancelPDF,
  onFileSelect,
  showSignInPrompt,
  hasPDFUploadLeft,
  pdfUploadLimit,
  pdfUploadsRemaining,
  pdfUploadsUsed
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const handlePromptKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onPromptSubmit();
    }
  };

  const handleFileChange = (e) => {
    onFileSelect(e);
  };

  const handlePDFClick = () => {
    onPDFUploadClick();
    fileInputRef.current?.click();
  };

  const getFileSizeString = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ✅ NEW: Check if any limits are reached
  const qrLimitReached = user && usageStats && !usageStats.canGenerate;
  const pdfLimitReached = user && !hasPDFUploadLeft && selectedFile;
  const dynamicLimitReached = isDynamicMode && !hasDynamicQRLeft;
  
  const currentPlan = planTracker?.planConfig?.name || 'Free';
  const isFreePlan = currentPlan.toLowerCase() === 'free';

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {showSignInPrompt && (
        <div className="sign-in-banner">
          <div className="sign-in-content">
            <span>💡 Sign in to save your QR code history</span>
            <button 
              className="sign-in-banner-btn"
              onClick={() => navigate('/')}
            >
              Sign In
            </button>
          </div>
        </div>
      )}

      <div className="prompt-section">
        {user && usageStats && !planTracker?.loading && (
          <div className="usage-banner">
            <div className="usage-banner-content">
              <span className="usage-banner-plan">{planTracker.planConfig.name}</span>
              <span className="dot">•</span>
              <span
                className="usage-banner-upgrade-link"
                onClick={() => navigate('/pricing-plans')}
              >
                Upgrade
              </span>
            </div>
          </div>
        )}

        <HelloSentences />
        
        <div className="prompt-wrapper">
          <div className="textarea-container">
            {selectedFile && (
              <div className="pdf-upload-preview">
                <div className="pdf-file-info">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="pdf-details">
                    <span className="pdf-name">{selectedFile.name}</span>
                    <span className="pdf-size">{getFileSizeString(selectedFile.size)}</span>
                  </div>
                </div>
                <button className="pdf-remove-btn" onClick={onCancelPDF}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            )}

            {isBulkProcessing && bulkProgress.total > 0 && (
              <div className="bulk-progress-bar">
                <div className="progress-header">
                  <span>Generating QR Codes...</span>
                  <span className="progress-count">{bulkProgress.current}/{bulkProgress.total}</span>
                </div>
                <div className="progress-bar-track">
                  <div 
                    className="progress-bar-fill"
                    style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <textarea
              className="prompt-textarea"
              placeholder="Describe what you want... (e.g., 'My website URL is example.com', 'Create a QR for my WiFi network', 'Contact card for John Doe')"
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              onKeyDown={handlePromptKeyDown}
              maxLength={1000}
              disabled={isAnyProcessing}
            />
            
            <div className="textarea-controls">
              <div className="textarea-left-controls">
                <button
                  className="upload-pdf-btn"
                  onClick={handlePDFClick}
                  disabled={isAnyProcessing}
                  title="Upload PDF for bulk QR generation"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Upload PDF</span>
                </button>
              </div>

              <div className="textarea-right-controls">
                {canUseDynamic && (
                  <div className="dynamic-toggle-inline">
                    <label className="dynamic-toggle-compact">
                      <input 
                        type="checkbox"
                        checked={isDynamicMode}
                        onChange={(e) => setIsDynamicMode(e.target.checked)}
                        disabled={!hasDynamicQRLeft}
                      />
                      <span className="toggle-slider-compact"></span>
                      <span className="toggle-label-compact">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Dynamic
                        {dynamicQRLimit > 0 && (
                          <span className="dynamic-count">
                            ({dynamicQRLimit === -1 ? '∞' : `${dynamicQRRemaining}/${dynamicQRLimit}`})
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                <button
                  className="send-btn"
                  onClick={onPromptSubmit}
                  disabled={isAnyProcessing || (promptText.trim().length === 0 && !selectedFile)}
                  title="Generate QR Code (Enter)"
                >
                  {isAnyProcessing ? (
                    <div className="spinner-small"></div>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M7 11L12 6L17 11M12 18V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ✅ NEW: Beautiful upgrade messages for different limit scenarios */}
          
          {/* QR Code Limit Reached */}
          {qrLimitReached && !selectedFile && (
            <div className="limit-upgrade-card">
              <div className="limit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="limit-content">
                <h4>You've reached your {currentPlan} plan limit</h4>
                <p>
                  {isFreePlan ? (
                    <>You've used all <strong>3 QR codes</strong> for this month. Upgrade to continue generating QR codes!</>
                  ) : (
                    <>You've used all your monthly QR codes. Upgrade to a higher plan for more QR codes!</>
                  )}
                </p>
              </div>
              <button 
                className="upgrade-btn-card"
                onClick={() => navigate('/pricing-plans')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Upgrade Now
              </button>
            </div>
          )}

          {/* PDF Upload Limit Reached */}
          {pdfLimitReached && (
            <div className="limit-upgrade-card pdf-limit">
              <div className="limit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 13h8M8 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="limit-content">
                <h4>PDF Upload Limit Reached</h4>
                <p>
                  {isFreePlan ? (
                    <>You've used your <strong>1 PDF upload</strong> for this month. Upgrade to Pro for <strong>5 PDFs/month</strong> or Premium for <strong>unlimited PDFs</strong>!</>
                  ) : pdfUploadLimit === 5 ? (
                    <>You've used all <strong>5 PDF uploads</strong> this month. Upgrade to Premium for <strong>unlimited PDF uploads</strong>!</>
                  ) : (
                    <>You've reached your PDF upload limit for this month. Upgrade for more uploads!</>
                  )}
                </p>
              </div>
              <button 
                className="upgrade-btn-card"
                onClick={() => navigate('/pricing-plans')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Upgrade Now
              </button>
            </div>
          )}

          {/* Dynamic QR Limit Reached */}
          {dynamicLimitReached && (
            <div className="limit-upgrade-card dynamic-limit">
              <div className="limit-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="limit-content">
                <h4>Dynamic QR Code Limit Reached</h4>
                <p>
                  {isFreePlan ? (
                    <>You've used your <strong>1 dynamic QR code</strong>. Upgrade to Pro or Premium for <strong>unlimited dynamic QR codes</strong>!</>
                  ) : (
                    <>You've used all your dynamic QR codes. Upgrade for unlimited dynamic QR codes!</>
                  )}
                </p>
              </div>
              <button 
                className="upgrade-btn-card"
                onClick={() => navigate('/pricing-plans')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Upgrade Now
              </button>
            </div>
          )}

          {/* Regular error messages */}
          {processingError && !qrLimitReached && (
            <div className="error-message">
              <span>⚠️</span>
              <span>{processingError}</span>
            </div>
          )}
          
          {pdfError && !pdfLimitReached && (
            <div className="error-message">
              <span>⚠️</span>
              <span>{pdfError}</span>
            </div>
          )}

          {bulkError && (
            <div className="error-message">
              <span>⚠️</span>
              <span>{bulkError}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PromptInput;